import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { Order } from "@/lib/supabase";
import { format } from "date-fns";

interface OrderPrintReportProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order | null;
}

export function OrderPrintReport({ isOpen, onClose, order }: OrderPrintReportProps) {
  if (!order) return null;

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'MM/dd/yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateString: string | undefined | null) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy HH:mm a');
    } catch (error) {
      return dateString;
    }
  };

  // Helper function to render checkbox
  const renderCheckbox = (checked: boolean) => (
    <span className="inline-block w-3 h-3 border border-black mr-2 text-center text-xs leading-3">
      {checked ? '✓' : ''}
    </span>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[210mm] max-h-[90vh] overflow-y-auto print:max-w-none print:max-h-none print:overflow-visible print:p-0">
        <DialogHeader className="print:hidden sticky top-0 bg-white z-50 border-b border-gray-200 pb-4">
          <DialogTitle className="flex items-center justify-between">
            <span>Facility Manipulation Order Report</span>
            <div className="flex space-x-2">
              <Button onClick={handlePrint} variant="outline" size="sm">
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <div className="bg-white text-black print:bg-white print:text-black text-xs leading-tight" style={{ fontFamily: 'Arial, sans-serif' }}>
          {/* Header */}
          <div className="text-center mb-4 print:mb-2">
            <div className="bg-gray-200 p-2 text-lg font-bold border border-black">
              FACILITY MANIPULATION ORDER REPORT
            </div>
          </div>

          {/* Company Info and Clinical Site Header */}
          <div className="flex justify-between mb-4 print:mb-2">
            <div className="w-1/2">
              <div className="flex items-center mb-2">
                <div className="w-12 h-12 border border-black rounded-full flex items-center justify-center mr-4 text-xs font-bold">
                  P
                </div>
                <div>
                  <div className="font-bold text-lg">Progenitor</div>
                  <div className="text-sm">Cell Therapy, LLC</div>
                </div>
              </div>
              <div className="text-xs space-y-1">
                <div><strong>Progenitor Cell Therapy, L.L.C.</strong></div>
                <div>Cellular Therapy Laboratory</div>
                <div>214 Pearl Court Suite B</div>
                <div>Allendale, NJ 07401</div>
                <div>Main: 201-345-3401</div>
                <div>Fax: 201-727-3433</div>
              </div>
            </div>
            
            <div className="w-1/2 text-right">
              <div className="text-lg font-bold mb-2">Facility Manipulation Order Form</div>
              <div className="border-t-2 border-b-2 border-black py-2 space-y-1 text-xs">
                <div><strong>Clinical Site:</strong> HUMC <span className="ml-8"><strong>Clinical Site #:</strong> NJ01</span></div>
                <div><strong>Address:</strong> 30 Prospect Avenue</div>
                <div className="ml-20">Hackensack, NJ 07601</div>
                <div><strong>Phone:</strong> (551) 996-2000 <span className="ml-4"><strong>Fax:</strong> 551-996-0002</span></div>
                <div><strong>Donor Information:</strong> XNA</div>
              </div>
            </div>
          </div>

          {/* Patient and Donor Information */}
          <div className="flex justify-between mb-4 print:mb-2">
            {/* Patient Information */}
            <div className="w-1/2 pr-4">
              <div className="border-b-2 border-black font-bold mb-2">Patient (Recipient) Information</div>
              
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span><strong>Last Name:</strong> <u>{order.patients?.last_name || ''}</u></span>
                  <span><strong>First:</strong> <u>{order.patients?.first_name || ''}</u></span>
                  <span><strong>MI:</strong> <u>{(order as any).patients?.middle_name || ''}</u></span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {renderCheckbox(order.patients?.gender === 'male')} Male
                    {renderCheckbox(order.patients?.gender === 'female')} Female
                  </div>
                  <span><strong>Patient Weight:</strong> <u>{(order as any).patients?.weight_kg || ''}</u> kg</span>
                </div>
                
                <div className="flex justify-between">
                  <span><strong>DOB:</strong> <u>{formatDate(order.patients?.date_of_birth)}</u></span>
                  <span><strong>Medical Record #:</strong> <u>{order.patients?.mrn || ''}</u></span>
                </div>
                
                <div className="flex justify-between">
                  <span><strong>Diagnosis/Stage:</strong> <u>{(order as any).diagnosis || ''}</u></span>
                  <span><strong>ABO/Rh:</strong> <u>{(order as any).patients?.abo_rh || ''}</u></span>
                </div>
                
                <div className="flex justify-between">
                  <span><strong>SSN:</strong> <u>{(order as any).patients?.ssn || ''}</u></span>
                  <span><strong>Physician:</strong> <u>{(order as any).patients?.physician || ''}</u></span>
                </div>
                
                <div className="flex justify-between">
                  <span><strong>Contact Person:</strong> <u>{order.patients?.emergency_contact_name || ''}</u></span>
                  <span><strong>Phone:</strong> <u>{order.patients?.emergency_contact_phone || ''}</u></span>
                </div>
              </div>
            </div>

            {/* Donor Information */}
            <div className="w-1/2 pl-4">
              <div className="space-y-2 text-xs">
                <div><strong>Last Name:</strong> <u>{(order as any).donor_last_name || ''}</u></div>
                <div><strong>First:</strong> <u>{(order as any).donor_first_name || ''}</u></div>
                <div><strong>MI:</strong> <u>{(order as any).donor_middle_name || ''}</u></div>
                
                <div className="flex items-center space-x-4 mt-2">
                  {renderCheckbox((order as any).donor_gender === 'male')} Male
                  {renderCheckbox((order as any).donor_gender === 'female')} Female
                  <span className="ml-8"><strong>Patient Weight:</strong> <u>{(order as any).donor_weight_kg || ''}</u> kg</span>
                </div>
                
                <div className="flex justify-between">
                  <span><strong>DOB:</strong> <u>{formatDate((order as any).donor_dob)}</u></span>
                  <span><strong>Medical Record #:</strong> <u>{(order as any).donor_mrn || ''}</u></span>
                </div>
                
                <div><strong>ABO/Rh:</strong> <u>{(order as any).donor_abo_rh || ''}</u></div>
                
                <div className="mt-4 font-bold">Allogeneic/Unrelated Donor Information (if applicable)</div>
                <div><strong>Last Name:</strong> ___________________</div>
                <div><strong>First:</strong> ___________________</div>
                <div><strong>MI:</strong> ________</div>
                
                <div className="flex items-center space-x-4">
                  <span>□ Male</span>
                  <span>□ Female</span>
                </div>
                
                <div><strong>DOB:</strong> ____/____/____</div>
                <div><strong>ABO/Rh:</strong> ___________________</div>
                <div><strong>Medical Record # or Donor ID:</strong> ___________________________</div>
              </div>
            </div>
          </div>

          {/* Processing Requirements */}
          <div className="mb-4 print:mb-2">
            <div className="border-b-2 border-black font-bold mb-2">Processing Requirements</div>
            
            <div className="text-xs space-y-1">
              <div><strong>Approximate date of initial collection/harvest:</strong> <u>{formatDate((order as any).collection_date)}</u></div>
              
              <div className="flex items-center space-x-1">
                {renderCheckbox((order as any).processing_within_24h || false)}
                <span>Anticipated date of processing within 24 hrs of collection</span>
              </div>
              
              <div className="flex items-center space-x-1">
                {renderCheckbox((order as any).processing_within_48h || false)}
                <span>Anticipated date of processing within 48 hrs of collection (international collections)</span>
              </div>
            </div>

            {/* Processing Procedures Grid */}
            <div className="flex justify-between mt-4 space-x-8">
              <div className="flex-1 space-y-2">
                <div className="font-bold">HPC-Apheresis</div>
                <div className="space-y-1 text-xs">
                  {['Accession and Evaluation (P-101)', 'Cryopreservation (P-102)', 'CD34 Selection (P-103)', 'T Cell Depletion (P-105)', 'T Cell Depletion/Cryopres (P-106)'].map((proc) => (
                    <div key={proc} className="flex items-center">
                      {renderCheckbox(((order as any).hpc_apheresis_procedures || []).includes(proc))}
                      {proc}
                    </div>
                  ))}
                </div>
                
                <div className="font-bold mt-4">HPC-Marrow</div>
                <div className="space-y-1 text-xs">
                  {['Accession and Evaluation (P-201)', 'Cryopreservation (P-202)', 'CD34 Selection (P-203)', 'CD34 Selection/Cryopres (P-204)', 'T Cell Depletion (P-205)', 'T Cell Depletion/Cryopres (P-206)', 'Plasma Depletion (P-208)', 'RBC Depletion (P-209)'].map((proc) => (
                    <div key={proc} className="flex items-center">
                      {renderCheckbox(((order as any).hpc_marrow_procedures || []).includes(proc))}
                      {proc}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex-1 space-y-2">
                <div className="font-bold">TC-T Cells (Donor Leukocytes)</div>
                <div className="space-y-1 text-xs">
                  {['Accession and Evaluation (P-301)', 'Cryopreservation (P-302)', 'CD34 Selection/Cryopres (P-104)'].map((proc) => (
                    <div key={proc} className="flex items-center">
                      {renderCheckbox(((order as any).tc_t_cells_procedures || []).includes(proc))}
                      {proc}
                    </div>
                  ))}
                </div>
                
                <div className="font-bold mt-4">HPC-Cord Blood (Cord Blood)</div>
                <div className="space-y-1 text-xs">
                  <div className="flex items-center">
                    {renderCheckbox(((order as any).hpc_cord_blood_procedures || []).includes('Thaw/prep for infusion (P-407)'))}
                    Thaw/prep for infusion (P-407)
                  </div>
                </div>
                
                <div className="font-bold mt-4">Common Assays (if not in P code)</div>
                <div className="space-y-1 text-xs">
                  {['CD3 Enumeration (A-203)', 'ABO/Rh Testing (A-801)', '7-AAD Viability Testing (A-204)', 'CD34 Enumeration (A-201)', 'CBC Enumeration (A-206)', 'CFU Enumeration (A-101)'].map((proc) => (
                    <div key={proc} className="flex items-center">
                      {renderCheckbox(((order as any).common_assays_procedures || []).includes(proc))}
                      {proc}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* PCT Information Box */}
            <div className="border border-black p-2 mt-4 text-xs bg-gray-100">
              <div className="font-bold">Please refer to PCT Cellular Therapy Directory of Services for a complete description of codes.</div>
              <div className="mt-2">For information regarding Clinical Research studies contact the Chief Scientific Officer.</div>
            </div>
          </div>

          {/* Packaging Section */}
          <div className="flex justify-between mb-4 print:mb-2">
            <div className="w-2/3">
              <div className="border-b-2 border-black font-bold mb-2">Packaging</div>
              
              <div className="text-xs space-y-2">
                <div>
                  <strong>Do you have special requirements for aliquoting final product?</strong>
                </div>
                <div className="flex items-center space-x-4 ml-4">
                  {renderCheckbox((order as any).special_aliquoting === true)} Yes (if yes, complete below)
                  {renderCheckbox((order as any).special_aliquoting === false)} No
                </div>
                
                <div className="mt-4">
                  <div className="flex items-center">
                    {renderCheckbox((order as any).cd34_dose_option)} <strong>CD34 dose in each container</strong>
                  </div>
                  <div className="ml-6 space-y-1">
                    <div className="flex items-center">
                      {renderCheckbox((order as any).cd34_split_evenly)} Split evenly
                    </div>
                    <div className="flex items-center space-x-4">
                      <span>□ Minimum dose/container _____________ x10⁶/kg</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span>□ Maximum dose/container _____________ x10⁶/kg</span>
                    </div>
                    <div className="flex items-center">
                      <span>□ Specific dose in each container (x10⁶/kg)</span>
                    </div>
                    
                    <div className="flex space-x-4 ml-4">
                      <span>Bag A ______</span>
                      <span>Bag B ______</span>
                      <span>Bag C ______</span>
                      <span>Bag D ______</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4">
                  <div className="flex items-center">
                    {renderCheckbox((order as any).cd3_dose_option)} <strong>CD3 dose in each container</strong>
                  </div>
                  <div className="ml-6 space-y-1">
                    <div className="flex items-center">
                      <span>□ Split evenly</span>
                    </div>
                    <div className="flex items-center">
                      <span>□ Specific dose in each container (x10⁷/kg)</span>
                    </div>
                    
                    <div className="flex space-x-4 ml-4">
                      <span>Bag A ______</span>
                      <span>Bag B ______</span>
                      <span>Bag C ______</span>
                      <span>Bag D ______</span>
                    </div>
                    <div className="flex space-x-4 ml-4">
                      <span>Bag E ______</span>
                      <span>Bag F ______</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4">
                  <div><strong>Additional Packaging Instructions</strong></div>
                  <div className="border border-black h-16 mt-1 p-1">
                    {(order as any).additional_packaging_instructions || ''}
                  </div>
                </div>
              </div>
            </div>

            {/* Order Tracking Info */}
            <div className="w-1/3 pl-4">
              <div className="border border-black p-4 text-xs space-y-2">
                <div><strong>Order Entered On:</strong> {formatDateTime(order.created_at)}</div>
                <div><strong>Order Entered by:</strong> {order.creator?.first_name} {order.creator?.last_name}</div>
                
                {order.approved_at && (
                  <>
                    <div><strong>Order Approved On:</strong> {formatDateTime(order.approved_at)}</div>
                    <div><strong>Order Approved by:</strong> {order.approver?.first_name} {order.approver?.last_name}</div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Collection Goals */}
          <div className="mb-4 print:mb-2">
            <div className="border-b-2 border-black font-bold mb-2">Collection Goals</div>
            
            <div className="text-xs space-y-1">
              <div>
                <strong>Intended TNC Dose:</strong> <u>{(order as any).intended_tnc_dose || ''}</u> x 10⁸ TNC/kg
              </div>
              <div>
                <strong>Intended CD34 Dose:</strong> <u>{(order as any).intended_cd34_dose || '5.0'}</u> x 10⁶ CD34/kg
              </div>
              <div>
                <strong>Intended CD3 Dose:</strong> <u>{(order as any).intended_cd3_dose || ''}</u> x 10⁷ CD3/kg
              </div>
            </div>
          </div>

          {/* ABO/Rh Determination */}
          <div className="mb-4 print:mb-2">
            <div className="border-b-2 border-black font-bold mb-2">ABO/Rh Determination (Check One)</div>
            
            <div className="text-xs space-y-2">
              <div className="flex items-start">
                {renderCheckbox((order as any).abo_rh_testing_option === 'collection')}
                <span className="ml-2">ABO/Rh testing is performed at the time of each collection on a specimen of the collection or donor peripheral blood. Documentation for each collection is included with the shipment.</span>
              </div>
              
              <div className="flex items-center">
                {renderCheckbox((order as any).abo_rh_testing_option === 'pct')}
                <span className="ml-2">ABO/Rh testing performed by PCT. Please order above.</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between text-xs mt-8">
            <div>
              <strong>Form Completed by/Date:</strong> _________________________________
            </div>
            <div>
              <strong>SOP 01.001-F1 Rev F</strong>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}